import { EEmailHealthStatus, EmailReportKpisOutDto } from '@app/swagger-types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { format } from 'date-fns';

import { EmailKpiTile, EmailKpiTileStatusTone } from '../../shared/EmailKpiTile';

const TONE: Record<EEmailHealthStatus, EmailKpiTileStatusTone> = {
  [EEmailHealthStatus.OK]: 'ok',
  [EEmailHealthStatus.WARNING]: 'warn',
  [EEmailHealthStatus.CRITICAL]: 'bad',
};

const ICON: Record<EEmailHealthStatus, React.ReactNode> = {
  [EEmailHealthStatus.OK]: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
  [EEmailHealthStatus.WARNING]: <WarningAmberIcon sx={{ fontSize: 13 }} />,
  [EEmailHealthStatus.CRITICAL]: <ErrorOutlineIcon sx={{ fontSize: 13 }} />,
};

const TEXT: Record<EEmailHealthStatus, string> = {
  [EEmailHealthStatus.OK]: 'normal',
  [EEmailHealthStatus.WARNING]: 'attention',
  [EEmailHealthStatus.CRITICAL]: 'over the hard limit',
};

const statusFor = (status: EEmailHealthStatus) => ({ tone: TONE[status], icon: ICON[status], text: TEXT[status] });

// admin-emailbeheer blok B (opdracht §4, "zes kengetallen"). Bounce/complaint get a status
// colour with icon+text - opdracht §4: "nooit kleur alleen". upgradesWithin72h added
// 2026-08-24 - see EmailReportKpisOutDto's own backend javadoc for the attribution rule.
export const EmailReportKpiCards: React.FC<{ kpis: EmailReportKpisOutDto; percentagesMeasuredSince: string }> = ({
  kpis,
  percentagesMeasuredSince,
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <EmailKpiTile label="Sent" value={kpis.sent.toLocaleString()} />
        <EmailKpiTile label="Delivered" value={`${kpis.deliveredPercent.toFixed(1)}%`} />
        <EmailKpiTile
          label="Bounce"
          value={`${kpis.bouncePercent.toFixed(1)}%`}
          status={statusFor(kpis.bounceStatus)}
        />
        <EmailKpiTile
          label="Complaints"
          value={`${kpis.complaintPercent.toFixed(2)}%`}
          status={statusFor(kpis.complaintStatus)}
        />
        <EmailKpiTile label="Unsubscribed" value={`${kpis.unsubscribedPercent.toFixed(1)}%`} />
        <EmailKpiTile
          label="Upgrades <72h"
          value={kpis.upgradesWithin72h.toLocaleString()}
          foot="attributed to a mail"
        />
      </div>
      <span className="text-xxs text-gray-400 italic">
        Sent is the true total for the whole period. Every percentage above only counts sends from{' '}
        {format(new Date(percentagesMeasuredSince), 'd MMM yyyy')} onward - a fix that day made delivery/open/click
        tracking reliable again; older sends mostly have no measured outcome and would only drag these percentages down.
      </span>
    </div>
  );
};
